import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@loot-os/ui/components/dialog";
import { Input } from "@loot-os/ui/components/input";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useState } from "react";

import { useTRPC } from "../lib/trpc";
import { useDebounced } from "../lib/useDebounced";

/** A shopper as `users.list` answers them, the fields a pick reads. */
export interface PickedShopper {
  id: string;
  externalId: string;
  username: string | null;
  email: string | null;
}

/**
 * A dialog that finds one of the environment's end users (`users.list`, by id, external id,
 * username or email) and runs `onPick` on one, each row with its own action button. A row
 * `pickedLabel` names reads as a badge instead (already on the list, already blocked). The dialog
 * closes once `onPick` resolves.
 */
export function ShopperPickerDialog({
  triggerLabel,
  title,
  description,
  actionLabel,
  pending,
  pickedLabel,
  onPick,
}: {
  triggerLabel: string;
  title: string;
  description: string;
  /** The row button's word: Add, Block. */
  actionLabel: string;
  pending: boolean;
  /** The badge for a shopper the action already applies to; undefined = pickable. */
  pickedLabel?: (shopper: PickedShopper) => string | undefined;
  onPick: (shopper: PickedShopper) => Promise<unknown>;
}) {
  const trpc = useTRPC();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search.trim());
  const users = useQuery({
    ...trpc.users.list.queryOptions(
      { limit: 20, search: debouncedSearch || undefined },
      // Keep the previous results visible while a new search resolves.
      { placeholderData: (previous) => previous },
    ),
    enabled: open,
  });
  const results = users.data?.data ?? [];

  const pick = (shopper: PickedShopper) => {
    // A refusal toasts through the mutation cache; the dialog stays open for another pick.
    void onPick(shopper).then(
      () => {
        setOpen(false);
        setSearch("");
      },
      () => {},
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch("");
      }}
    >
      <DialogTrigger render={<Button type="button" size="sm" />}>
        <Plus />
        {triggerLabel}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <Input
          type="search"
          aria-label="Search shoppers"
          placeholder="Search username, external id or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div
          aria-busy={users.isPlaceholderData}
          className={`max-h-80 overflow-y-auto rounded-md border transition-opacity ${users.isPlaceholderData ? "opacity-60" : ""}`}
        >
          {users.error ? (
            <p className="px-3 py-6 text-center text-sm text-destructive">{users.error.message}</p>
          ) : users.isPending ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">Loading shoppers…</p>
          ) : results.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              {debouncedSearch
                ? `No shoppers match “${debouncedSearch}”.`
                : "This environment has no shoppers yet."}
            </p>
          ) : (
            <ul className="divide-y">
              {results.map((user) => {
                const picked = pickedLabel?.(user);
                return (
                  <li key={user.id} className="flex items-center justify-between gap-3 px-3 py-2">
                    <div className="grid min-w-0 gap-0.5">
                      <span className="flex items-center gap-2 text-sm">
                        <span className={user.username ? "font-medium" : "text-muted-foreground"}>
                          {user.username ?? "No username"}
                        </span>
                        {user.lockedAt && <Badge variant="destructive">locked</Badge>}
                      </span>
                      <span className="truncate font-mono text-xs text-muted-foreground">
                        {user.externalId}
                        {user.email ? ` · ${user.email}` : ""}
                      </span>
                    </div>
                    {picked ? (
                      <Badge variant="secondary">{picked}</Badge>
                    ) : (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={pending}
                        onClick={() => pick(user)}
                      >
                        {actionLabel}
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
